import type { Client } from '@frontend/api-types';
import type { SentryBreadcrumb } from '@frontend/duck-tape';
import type * as SentryReact from '@sentry/react';

const CRITICAL_JOURNEY_PREFIXES = ['/login', '/sign-up', '/sso-callback', '/onboarding', '/app/task'] as const;
const MODULE_SCRIPT_IMPORT_ERROR_MESSAGE = 'Importing a module script failed.';
const VITE_PRELOAD_ERROR_RELOAD_COOLDOWN_MS = 30 * 1000;
const VITE_PRELOAD_ERROR_RELOAD_STORAGE_KEY = 'duckbill:vite-preload-error-reloaded-url';
const VITE_PRELOAD_ERROR_NAME = 'VitePreloadError';

// eslint-disable-next-line fp/no-let
let isVitePreloadErrorHandlerRegistered = false;

// eslint-disable-next-line fp/no-let
let sentryLoadPromise: MaybeUndefined<Promise<SentryModule>>;

type SentryModule = typeof SentryReact;
type SeverityLevel = NonNullable<SentryBreadcrumb['level']>;

type VitePreloadErrorEvent = Event & {
  payload?: unknown;
};

type VitePreloadErrorReloadState = {
  timestamp: number;
  url: string;
};

const loadSentry = (): Promise<SentryModule> => {
  if (!sentryLoadPromise) {
    // eslint-disable-next-line fp/no-mutation
    sentryLoadPromise = import('@sentry/react');
  }

  return sentryLoadPromise;
};

const withSentry = (callback: (sentry: SentryModule) => void) => {
  void loadSentry()
    .then(callback)
    .catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('Failed to load Sentry.', error);
    });
};

const flushSentry = (timeout: number): Promise<boolean> =>
  loadSentry()
    .then((Sentry) => Sentry.flush(timeout))
    .catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('Failed to flush Sentry.', error);
      return false;
    });

export const isCriticalJourney = (transactionName: string): boolean =>
  CRITICAL_JOURNEY_PREFIXES.some((prefix) => transactionName === prefix || transactionName.startsWith(`${prefix}/`));

const getOriginalExceptionMessage = (originalException: unknown): MaybeUndefined<string> => {
  if (originalException instanceof Error) {
    return originalException.message;
  }

  if (typeof originalException === 'string') {
    return originalException;
  }

  return undefined;
};

const getBrowserModuleLoadContext = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    isOnline: window.navigator.onLine,
    path: window.location.pathname,
    url: window.location.href,
    userAgent: window.navigator.userAgent,
    visibilityState: window.document.visibilityState,
  };
};

export const initSentry = ({
  dsn,
  env,
  ignoreErrors = [],
}: {
  dsn: string;
  env: EnvType;
  ignoreErrors?: Array<RegExp | string>;
}) => {
  if (env === 'local') {
    // eslint-disable-next-line no-console
    console.info("Mock initSentry because it's disabled in local environments");
    return;
  }

  withSentry((Sentry) => {
    // Avoid re-initializing Sentry
    if (Sentry.getClient()) {
      return;
    }

    Sentry.init({
      beforeSend: (event, hint) => {
        const exceptionMessage =
          getOriginalExceptionMessage(hint.originalException) ?? event.exception?.values?.[0]?.value;

        if (exceptionMessage !== MODULE_SCRIPT_IMPORT_ERROR_MESSAGE) {
          return event;
        }

        return {
          ...event,
          contexts: {
            ...event.contexts,
            module_script_import_error: {
              ...getBrowserModuleLoadContext(),
              message: exceptionMessage,
            },
          },
          tags: {
            ...event.tags,
            module_load_error: 'true',
            module_load_error_name: VITE_PRELOAD_ERROR_NAME,
            module_script_import_error: 'true',
          },
        };
      },
      dsn,
      environment: env,
      ignoreErrors,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampler: (samplingContext) => (isCriticalJourney(samplingContext.name) ? 0.5 : 0.1),
    });
  });
};

const getVitePreloadErrorPayload = (event: Event): unknown => {
  if ('payload' in event) {
    return (event as VitePreloadErrorEvent).payload;
  }

  if (typeof CustomEvent !== 'undefined' && event instanceof CustomEvent) {
    return event.detail;
  }

  return undefined;
};

const getErrorFromVitePreloadEvent = (event: Event): Error => {
  const payload = getVitePreloadErrorPayload(event);

  if (payload instanceof Error) {
    return payload;
  }

  return new Error('Vite preload error');
};

const getSessionStorageValue = (storage: Storage, key: string): MaybeUndefined<string> => {
  try {
    return storage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const trySetSessionStorageValue = (storage: Storage, key: string, value: string): boolean => {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getVitePreloadErrorReloadState = (value: MaybeUndefined<string>): MaybeUndefined<VitePreloadErrorReloadState> => {
  if (!value) {
    return undefined;
  }

  try {
    const parsedValue: unknown = JSON.parse(value);

    if (!isRecord(parsedValue)) {
      return undefined;
    }

    const { timestamp, url } = parsedValue;

    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || typeof url !== 'string') {
      return undefined;
    }

    return { timestamp, url };
  } catch {
    return undefined;
  }
};

const getReloadBlockedReason = ({
  isOnline,
  previousReloadState,
  timestamp,
  url,
}: {
  isOnline: boolean;
  previousReloadState: MaybeUndefined<VitePreloadErrorReloadState>;
  timestamp: number;
  url: string;
}): MaybeUndefined<string> => {
  if (!isOnline) {
    return 'offline';
  }

  if (
    previousReloadState?.url === url &&
    timestamp - previousReloadState.timestamp < VITE_PRELOAD_ERROR_RELOAD_COOLDOWN_MS
  ) {
    return 'recently_reloaded';
  }

  return undefined;
};

const captureVitePreloadError = ({
  env,
  error,
  event,
  reloadBlockedReason,
  targetWindow,
}: {
  env: EnvType;
  error: Error;
  event: Event;
  reloadBlockedReason: MaybeUndefined<string>;
  targetWindow: Window;
}) => {
  const payload = getVitePreloadErrorPayload(event);
  const isReloadTriggered = !reloadBlockedReason;

  withSentry((Sentry) => {
    Sentry.withScope((scope) => {
      scope.setTag('module_load_error', 'true');
      scope.setTag('module_load_error_name', VITE_PRELOAD_ERROR_NAME);
      scope.setTag('reload_triggered', String(isReloadTriggered));
      scope.setTag('vite_preload_error', 'true');
      scope.setLevel('warning');
      scope.setContext('vite_preload_error', {
        env,
        isOnline: targetWindow.navigator.onLine,
        path: targetWindow.location.pathname,
        payloadMessage: payload instanceof Error ? payload.message : undefined,
        payloadName: payload instanceof Error ? payload.name : undefined,
        payloadType: typeof payload,
        reloadBlockedReason,
        url: targetWindow.location.href,
        userAgent: targetWindow.navigator.userAgent,
        visibilityState: targetWindow.document.visibilityState,
      });

      Sentry.captureException(error);
    });
  });
};

export const addSentryBreadcrumb = (breadcrumb: SentryBreadcrumb) => {
  withSentry((Sentry) => Sentry.addBreadcrumb(breadcrumb));
};

export const initVitePreloadErrorHandler = ({ env, targetWindow }: { env: EnvType; targetWindow?: Window }) => {
  const windowContext = targetWindow ?? (typeof window === 'undefined' ? undefined : window);

  if (!windowContext || isVitePreloadErrorHandlerRegistered) {
    return;
  }

  // eslint-disable-next-line fp/no-mutation
  isVitePreloadErrorHandlerRegistered = true;

  windowContext.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();

    const url = windowContext.location.href;
    const timestamp = Date.now();
    const previousReloadState = getVitePreloadErrorReloadState(
      getSessionStorageValue(windowContext.sessionStorage, VITE_PRELOAD_ERROR_RELOAD_STORAGE_KEY),
    );
    const reloadBlockedReasonBeforeStorage = getReloadBlockedReason({
      isOnline: windowContext.navigator.onLine,
      previousReloadState,
      timestamp,
      url,
    });
    const reloadBlockedReason =
      reloadBlockedReasonBeforeStorage ??
      (trySetSessionStorageValue(
        windowContext.sessionStorage,
        VITE_PRELOAD_ERROR_RELOAD_STORAGE_KEY,
        JSON.stringify({ timestamp, url }),
      )
        ? undefined
        : 'session_storage_unavailable');

    captureVitePreloadError({
      env,
      error: getErrorFromVitePreloadEvent(event),
      event,
      reloadBlockedReason,
      targetWindow: windowContext,
    });

    if (reloadBlockedReason) {
      return;
    }

    void flushSentry(2000).finally(() => windowContext.location.reload());
  });

  windowContext.addEventListener(
    'error',
    (event) => {
      if (event.message !== MODULE_SCRIPT_IMPORT_ERROR_MESSAGE) {
        return;
      }

      addSentryBreadcrumb({
        category: 'module-load',
        data: {
          env,
          filename: event.filename,
          isOnline: windowContext.navigator.onLine,
          path: windowContext.location.pathname,
          userAgent: windowContext.navigator.userAgent,
        },
        level: 'warning',
        message: 'Browser reported a module script import failure',
      });
    },
    { capture: true },
  );
};

export const identifySentry = ({ email, env, id }: Pick<Client, 'email' | 'id'> & { env: EnvType }) => {
  if (env !== 'local') {
    withSentry((Sentry) => {
      Sentry.setUser({
        email,
        id,
      });
    });
  } else {
    // eslint-disable-next-line no-console
    console.info("Mock identifySentry because it's disabled in local environments");
  }
};

// We can probably capture local errors too, for testing purposes?
export const captureSentryError = (error: Error) => {
  withSentry((Sentry) => Sentry.captureException(error));
};

export const captureSentryException = (exception: unknown) => {
  withSentry((Sentry) => Sentry.captureException(exception));
};

export const captureSentryMessage = (message: string, severity: SeverityLevel = 'info') =>
  withSentry((Sentry) => Sentry.captureMessage(message, severity));

export const setOnboardingStep = (step: string) => {
  withSentry((Sentry) => Sentry.setTag('onboarding_step', step));
};

export const markOnboardingCompleted = () => {
  withSentry((Sentry) => Sentry.setTag('onboarding_completed', 'true'));
};
